import React, { useEffect, useState } from 'react';
import { Flex } from 'rebass';
import Layout from '../Layout';
import { Container } from '../UI';
import { AddButton, ModalActionButton } from '../UI/Button';
import strings from '../../constants/strings';
import { NoQuestionGroups } from './NoQuestionGroups';
import { LoadingCaption } from '../UI/Loaders';
import TestLibraryQuestionGroupGrid from './QuestionGroup/Grid';
import {
  useCreateQuestionGroup,
  useQuestionGroups,
} from '../../contexts/api/question-group';
import { useSnackbar } from '../../contexts/ui';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import routes from '../../routes';

const SEARCH_QUERY_STORAGE_KEY = 'testLibraryQuestionGroupsSearchQuery';

const normalizeSearchValue = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const SearchBar = ({ query, setQuery }) => {
  const handleSearch = (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
  };

  const handleClear = () => {
    setQuery('');
  };

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        width: '260px',
      }}
    >
      <input
        type="text"
        placeholder="Filter"
        value={query}
        onChange={handleSearch}
        style={{
          width: '100%',
          paddingRight: '32px',
          boxSizing: 'border-box',
        }}
      />
      {query ? (
        <IconButton
          size="small"
          onClick={handleClear}
          style={{
            position: 'absolute',
            right: '4px',
            padding: '4px',
          }}
          aria-label="Clear filter"
        >
          x
        </IconButton>
      ) : null}
    </div>
  );
};

export const TestLibrary = () => {
  const { questionGroups, loading, error } = useQuestionGroups();
  const { showSnackbar } = useSnackbar();
  const [modal, setModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [query, setQuery] = useState(() => sessionStorage.getItem(SEARCH_QUERY_STORAGE_KEY) || '');
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const [isTypingSearch, setIsTypingSearch] = useState(false);
  const [filteredQuestionGroups, setFilteredQuestionGroups] = useState(questionGroups);
  const navigate = useNavigate();
  const { createQuestionGroup, loading: createInProgress } =
    useCreateQuestionGroup();
  const { getQuestionGroups } = useQuestionGroups();

  useEffect(() => {
    if (error) {
      showSnackbar(error, 'error');
    }
  }, [error, showSnackbar]);

  useEffect(() => {
    sessionStorage.setItem(SEARCH_QUERY_STORAGE_KEY, query);
  }, [query]);

  useEffect(() => {
    if (!isTypingSearch) {
      setDebouncedQuery(query);
      return;
    }

    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query);
      setIsTypingSearch(false);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [query, isTypingSearch]);

  useEffect(() => {
    if (debouncedQuery.trim() === '') {
      setFilteredQuestionGroups(questionGroups);
      return;
    }

    const normalizedQuery = normalizeSearchValue(debouncedQuery);
    const filteredQuestionGroups = questionGroups.filter((questionGroup) =>
      normalizeSearchValue(questionGroup.name).includes(normalizedQuery)
    );
    setFilteredQuestionGroups(filteredQuestionGroups);
  }, [debouncedQuery, questionGroups]);

  const hasQuestionGroups = !loading && questionGroups.length > 0;

  const handleCreateQuestionGroup = async () => {
    const { success, questionGroup } = await createQuestionGroup({
      name: newGroupName,
    });
    if (success) {
      showSnackbar(
        strings.en.testLibrary.addQuestionGroup.successMessage(newGroupName),
      );
      await getQuestionGroups();
      navigate(routes.client.editTestSet(questionGroup.id));
    } else {
      showSnackbar(
        strings.en.testLibrary.addQuestionGroup.errorMessage(newGroupName),
        'error',
      );
    }
  };

  return (
    <Layout>
      <Container>
        <Flex justifyContent="space-between" alignItems="center" mb={3}>
          <SearchBar
            query={query}
            setQuery={(newQuery) => {
              setIsTypingSearch(true);
              setQuery(newQuery);
            }}
          />
          <AddButton size="s" onClick={() => setModal(true)}>
            {strings.en.testLibrary.addQuestionGroup.title}
          </AddButton>
        </Flex>
        {loading ? (
          <LoadingCaption />
        ) : (
          <TestLibraryQuestionGroupGrid questionGroups={filteredQuestionGroups} />
        )}
        {!hasQuestionGroups && !loading && <NoQuestionGroups />}
      </Container>
      <Dialog
        onClose={() => setModal(false)}
        aria-labelledby="customized-dialog-title"
        open={modal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          id="customized-dialog-title"
          onClose={() => setModal(false)}
        >
          {strings.en.testLibrary.addQuestionGroup.title}
        </DialogTitle>
        <DialogContent id="dialog-content" dividers>
          <div
            style={{
              minHeight: '300px',
              maxWidth: '620px',
              textAlign: 'center',
              margin: '0 auto',
            }}
          >
            <p>{strings.en.testLibrary.addQuestionGroup.nameInfo}</p>
            <br></br>
            <TextField
              label="Question Group Name"
              name="firstName"
              variant="outlined"
              style={{ width: '100%' }}
              size="small"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <ModalActionButton ghost size="s" onClick={() => setModal(false)}>
            Cancel
          </ModalActionButton>
          <ModalActionButton
            size="s"
            onClick={handleCreateQuestionGroup}
            disabled={createInProgress || newGroupName.trim() === ''}
          >
            Next
          </ModalActionButton>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default TestLibrary;
